import * as React from "react";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type StateVariant = "error" | "warning" | "loading";

type StatePanelRetry = {
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
} & (
  | { onClick: () => void; href?: never }
  | { href: string; onClick?: never }
);

interface StatePanelProps {
  variant?: StateVariant;
  title: string;
  titleAs?: "div" | "h1" | "h2" | "p";
  message?: string;
  lastUpdate?: string;
  retry?: StatePanelRetry;
  secondary?: StatePanelRetry;
  shape?: "banner" | "page";
  icon?: React.ReactNode;
  className?: string;
  role?: "alert" | "status";
}

const toneText: Record<StateVariant, string> = {
  error: "text-destructive",
  warning: "text-warning",
  loading: "text-muted-foreground",
};

const bannerTone: Record<StateVariant, string> = {
  error: "border-destructive/30 bg-destructive/5",
  warning: "border-warning/30 bg-warning/5",
  loading: "border-white/[0.08] bg-surface-raised",
};

const announcementProps: Record<
  StateVariant,
  Pick<React.HTMLAttributes<HTMLDivElement>, "role" | "aria-live" | "aria-atomic">
> = {
  error: { role: "alert", "aria-live": "assertive", "aria-atomic": true },
  warning: { role: "status", "aria-live": "polite", "aria-atomic": true },
  loading: { role: "status", "aria-live": "polite", "aria-atomic": true },
};

function RetryControl({
  retry,
  defaultLabel,
  variant,
  className,
}: {
  retry: StatePanelRetry;
  defaultLabel: string;
  variant: React.ComponentProps<typeof Button>["variant"];
  className?: string;
}) {
  const isLoading = retry.isLoading === true;
  const isDisabled = retry.disabled === true || isLoading;
  const label = retry.label ?? defaultLabel;
  const content = (
    <>
      {isLoading && <Spinner size="xs" />}
      <span>{label}</span>
    </>
  );

  if ("href" in retry && !isDisabled) {
    return (
      <Button asChild variant={variant} size="sm" className={cn("gap-1.5", className)}>
        <a href={retry.href}>{content}</a>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      onClick={"onClick" in retry ? retry.onClick : undefined}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      className={cn("gap-1.5", className)}
    >
      {content}
    </Button>
  );
}

export function StatePanel({
  variant = "error",
  title,
  titleAs = "div",
  message,
  lastUpdate,
  retry,
  secondary,
  shape = "banner",
  icon,
  className,
  role,
}: StatePanelProps) {
  const TitleTag = titleAs;
  const liveProps = role
    ? { role, "aria-live": role === "alert" ? "assertive" as const : "polite" as const, "aria-atomic": true }
    : announcementProps[variant];
  const defaultIcon =
    variant === "loading" ? (
      <Spinner size={shape === "page" ? "lg" : "sm"} />
    ) : variant === "warning" ? (
      <AlertTriangle className="size-5" />
    ) : (
      <AlertCircle className="size-5" />
    );

  if (shape === "page") {
    return (
      <div
        {...liveProps}
        className={cn(
          "flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center",
          className,
        )}
      >
        <div className={toneText[variant]}>{icon ?? defaultIcon}</div>
        <div className="space-y-1">
          <TitleTag className="text-base font-semibold text-foreground">{title}</TitleTag>
          {message && (
            <div className="mx-auto max-w-md text-sm text-muted-foreground">{message}</div>
          )}
          {lastUpdate && (
            <div className="text-xs text-muted-foreground">{lastUpdate}</div>
          )}
        </div>
        {retry || secondary ? (
          <div className="mt-1 flex flex-col items-center gap-2 sm:flex-row">
            {retry ? (
              <RetryControl
                retry={retry}
                defaultLabel="Try again"
                variant={variant === "error" ? "default" : "outline"}
              />
            ) : null}
            {secondary ? (
              <RetryControl retry={secondary} defaultLabel="Sign In" variant="secondary" />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      {...liveProps}
      className={cn(
        "flex w-full items-start gap-3 rounded-panel border px-4 py-3 surface-toplight",
        bannerTone[variant],
        className,
      )}
    >
      <div className={cn("mt-0.5 shrink-0", toneText[variant])}>{icon ?? defaultIcon}</div>
      <div className="min-w-0 flex-1">
        <TitleTag className="text-sm font-semibold text-foreground">{title}</TitleTag>
        {message && <div className="text-xs text-muted-foreground">{message}</div>}
        {lastUpdate && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">{lastUpdate}</div>
        )}
      </div>
      {retry || secondary ? (
        <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row">
          {retry ? <RetryControl retry={retry} defaultLabel="Retry" variant="outline" /> : null}
          {secondary ? (
            <RetryControl retry={secondary} defaultLabel="Sign In" variant="secondary" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ErrorBanner(props: Omit<StatePanelProps, "variant">) {
  return <StatePanel variant="error" {...props} />;
}
